import React from 'react';

import { useClaimContext } from '~/components/Claim/ClaimContext';
import Tag from '~/core/atoms/Tag/Tag';
import FileLinkButton from '~/core/molecules/FileLinkButton';
import Table, { ColumnDef, TableRowData } from '~/core/molecules/Table';
import { tryFormatDateString } from '~/utils/Dates/DateTimeUtils';

export interface ClaimDocumentMetadata extends TableRowData {
  id: string;
  document_name?: string;
  document_type?: string;
  document_summary?: string;
  file_type?: string;
  upload_date?: string;
  is_translated?: boolean;
}

interface DocumentsTableProps {
  documents: ClaimDocumentMetadata[];
  isLoading: boolean;
  onSelectionChange: (selectedRows: ClaimDocumentMetadata[]) => void;
  className?: string;
  selectedDocuments?: ClaimDocumentMetadata[];
}

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  documents,
  isLoading,
  onSelectionChange,
  className,
  selectedDocuments,
}) => {
  const { claimNumber } = useClaimContext();
  const columns: ColumnDef<ClaimDocumentMetadata>[] = [
    {
      id: 'id',
      header: 'View File',
      accessorKey: 'id',
      width: 90,
      sortable: false,
      cell: (value, row) => {
        return value ? (
          <div className="flex items-center justify-center">
            <FileLinkButton
              originalFile={{
                id: row.id,
                url: `/api/v1/claims/${claimNumber}/documents/${row.id}/public_url`,
              }}
            />
          </div>
        ) : null;
      },
    },
    {
      id: 'file_type',
      header: 'File Type',
      accessorKey: 'file_type',
      width: 90,
      sortable: true,
      cell: (value) => (value ? <Tag text={value} /> : null),
    },

    {
      id: 'document_name',
      header: 'File Name',
      accessorKey: 'document_name',
      width: 200,
      sortable: true,
      cell: (value) => (
        <span className="text-grey-900 text-sm font-medium" title={value}>
          {value ? (value?.length > 20 ? value.slice(0, 20) + '...' : value) : 'Unnamed Document'}
        </span>
      ),
    },
    {
      id: 'document_summary',
      header: 'Summary',
      accessorKey: 'document_summary',
      sortable: false,
      cell: (value) => (
        <span className="text-grey-700 text-sm" title={value}>
          {value ? (value?.length > 80 ? value.slice(0, 80) + '...' : value) : ''}
        </span>
      ),
    },
    {
      id: 'upload_date',
      header: 'Created Date',
      accessorKey: 'upload_date',
      width: 150,
      sortable: true,
      cell: (value) => <span className="text-grey-600 text-sm">{tryFormatDateString(value)}</span>,
    },
  ];

  return (
    <Table
      data={documents}
      columns={columns}
      loading={isLoading}
      selection={{
        enabled: true,
        showSelectAll: true,
        selectedRows: selectedDocuments,
      }}
      sorting={{
        enabled: true,
        defaultSort: [{ id: 'upload_date', desc: true }],
      }}
      onSelectionChange={onSelectionChange}
      className={className}
    />
  );
};

export default DocumentsTable;
